/**
 * Cache Service
 *
 * Centralized caching layer for the Vue 3 SPA.
 * Features:
 * - IndexedDB storage for persistence across sessions
 * - TTL-based expiration with configurable durations
 * - Event-based invalidation via eventBus
 * - Cross-tab synchronization via BroadcastChannel
 * - Auto-refresh for user-scoped data
 * - Stale-while-revalidate pattern support
 */

import { reactive } from 'vue'
import { indexedDBAdapter, type CacheEntry } from './IndexedDBAdapter'
import { cacheSyncManager } from './CacheSyncManager'
import { CACHE_CONFIGS, CACHE_DEFAULTS } from '../../config/cacheConfig'
import { eventBus } from '../../eventBus'
import { log } from '../../utils/DebugLogger'

export interface CacheOptions {
  ttl?: number
  storage?: 'memory' | 'indexedDB'
  staleWhileRevalidate?: boolean
  autoRefresh?: boolean
  checkInterval?: number
  events?: string[]
  [key: string]: unknown
}

export interface CacheStats {
  hits: number
  misses: number
  evictions: number
  hitRate?: number
  memoryEntries?: number
  totalEntries?: number
  expiredEntries?: number
  validEntries?: number
  keys?: string[]
  isInitialized?: boolean
  crossTabSyncAvailable?: boolean
}

export interface CacheState {
  isInitialized: boolean
  stats: { hits: number; misses: number; evictions: number }
}

const memoryCache = new Map<string, CacheEntry>()

export const cacheState: CacheState = reactive({
  isInitialized: false,
  stats: {
    hits: 0,
    misses: 0,
    evictions: 0
  }
})

class CacheService {
  private refreshTimers = new Map<string, ReturnType<typeof setInterval>>()
  private pendingFetches = new Map<string, Promise<unknown>>()
  isInitialized = false

  async init(): Promise<void> {
    if (this.isInitialized) return

    try {
      await indexedDBAdapter.init()
      cacheSyncManager.init(this)
      this.setupEventListeners()
      await indexedDBAdapter.cleanupExpired()
      this.startAutoRefreshTimers()

      this.isInitialized = true
      cacheState.isInitialized = true

      log('CacheService', 'init', 'Cache service initialized')
    } catch (error) {
      log('CacheService', 'init', 'Initialization failed', error)
      throw error
    }
  }

  async get<T = unknown>(key: string, fetchFn: () => Promise<T>, options: CacheOptions = {}): Promise<T> {
    await this.init()

    const config = this.getConfig(key, options)
    const now = Date.now()

    if (memoryCache.has(key)) {
      const entry = memoryCache.get(key)!
      if (entry.expiry > now) {
        cacheState.stats.hits++
        log('CacheService', 'get', `Memory HIT: ${key}`, { age: now - entry.createdAt })
        return entry.value as T
      }
      memoryCache.delete(key)
    }

    const stored = await indexedDBAdapter.get<T>(key)
    if (stored && stored.expiry > now) {
      cacheState.stats.hits++
      log('CacheService', 'get', `IndexedDB HIT: ${key}`, { age: now - stored.createdAt })

      memoryCache.set(key, stored as CacheEntry)

      return stored.value
    }

    if (config.staleWhileRevalidate && stored) {
      log('CacheService', 'get', `Stale-while-revalidate: ${key}`)

      memoryCache.set(key, stored as CacheEntry)

      this.fetchAndStore(key, fetchFn, config).catch(err => {
        log('CacheService', 'get', `Background refresh failed: ${key}`, err)
      })

      return stored.value
    }

    cacheState.stats.misses++
    log('CacheService', 'get', `MISS: ${key}`)

    return this.fetchAndStore(key, fetchFn, config)
  }

  async fetchAndStore<T = unknown>(key: string, fetchFn: () => Promise<T>, config: CacheOptions): Promise<T> {
    if (this.pendingFetches.has(key)) {
      log('CacheService', 'fetchAndStore', `Waiting for pending fetch: ${key}`)
      return this.pendingFetches.get(key) as Promise<T>
    }

    const fetchPromise = (async () => {
      try {
        const value = await fetchFn()
        await this.set(key, value, config)
        cacheSyncManager.broadcastUpdate(key)
        return value
      } finally {
        this.pendingFetches.delete(key)
      }
    })()

    this.pendingFetches.set(key, fetchPromise)
    return fetchPromise
  }

  async set<T = unknown>(key: string, value: T, options: CacheOptions = {}): Promise<void> {
    await this.init()

    const config = this.getConfig(key, options)
    const now = Date.now()
    const ttl = (config.ttl as number) || (CACHE_DEFAULTS as { ttl: number }).ttl
    const expiry = now + ttl

    const entry: CacheEntry<T> = {
      key,
      value,
      expiry,
      createdAt: now,
      updatedAt: now
    }

    memoryCache.set(key, entry as CacheEntry)

    if (config.storage !== 'memory') {
      await indexedDBAdapter.set(key, value, expiry)
    }

    log('CacheService', 'set', `Stored: ${key}`, { ttl, expiry })
  }

  async invalidate(key: string): Promise<void> {
    await this._invalidateLocal(key)
    cacheSyncManager.broadcastInvalidate(key)
  }

  async _invalidateLocal(key: string): Promise<void> {
    memoryCache.delete(key)
    await indexedDBAdapter.delete(key)
    cacheState.stats.evictions++

    log('CacheService', 'invalidate', `Invalidated: ${key}`)
  }

  async invalidatePattern(pattern: string): Promise<number> {
    const count = await this._invalidatePatternLocal(pattern)
    cacheSyncManager.broadcastInvalidatePattern(pattern)
    return count
  }

  async _invalidatePatternLocal(pattern: string): Promise<number> {
    const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$')

    let count = 0
    for (const key of memoryCache.keys()) {
      if (regex.test(key)) {
        memoryCache.delete(key)
        count++
      }
    }

    const dbCount = await indexedDBAdapter.deletePattern(pattern)

    cacheState.stats.evictions += count + dbCount
    log('CacheService', 'invalidatePattern', `Pattern: ${pattern}`, { memoryCount: count, dbCount })

    return count + dbCount
  }

  async clear(): Promise<void> {
    await this._clearLocal()
    cacheSyncManager.broadcastClear()
  }

  async _clearLocal(): Promise<void> {
    const count = memoryCache.size
    memoryCache.clear()

    await indexedDBAdapter.clear()

    this.stopAutoRefreshTimers()

    cacheState.stats.evictions += count
    log('CacheService', 'clear', 'All cache cleared', { count })
  }

  getConfig(key: string, overrides: CacheOptions = {}): CacheOptions {
    const configKey = this.findConfigKey(key)
    const baseConfig = configKey ? (CACHE_CONFIGS as Record<string, CacheOptions>)[configKey] : {}

    return {
      ...(CACHE_DEFAULTS as unknown as CacheOptions),
      ...baseConfig,
      ...overrides
    }
  }

  findConfigKey(key: string): string | null {
    const configs = CACHE_CONFIGS as Record<string, CacheOptions>
    if (configs[key]) {
      return key
    }

    for (const configKey of Object.keys(configs)) {
      if (configKey.includes('*')) {
        const regex = new RegExp('^' + configKey.replace(/\*/g, '.*') + '$')
        if (regex.test(key)) {
          return configKey
        }
      }
    }

    return null
  }

  setupEventListeners(): void {
    Object.entries(CACHE_CONFIGS as Record<string, CacheOptions>).forEach(([key, config]) => {
      if (config.events) {
        config.events.forEach(eventName => {
          (eventBus as { on: (e: string, fn: () => void) => void }).on(eventName, () => {
            log('CacheService', 'event', `${eventName} -> invalidating ${key}`)
            this.invalidate(key)
          })
        })
      }
    })

    ;(eventBus as { on: (e: string, fn: () => void) => void }).on('user.logout', () => {
      log('CacheService', 'event', 'Logout -> clearing all cache')
      this.clear()
    })

    log('CacheService', 'setupEventListeners', 'Event listeners registered')
  }

  startAutoRefreshTimers(): void {
    Object.entries(CACHE_CONFIGS as Record<string, CacheOptions>).forEach(([key, config]) => {
      if (config.autoRefresh && config.checkInterval) {
        const timer = setInterval(async () => {
          await this.checkAndRefresh(key, config)
        }, config.checkInterval as number)

        this.refreshTimers.set(key, timer)
        log('CacheService', 'startAutoRefreshTimers', `Timer started for ${key}`, { interval: config.checkInterval })
      }
    })
  }

  stopAutoRefreshTimers(): void {
    this.refreshTimers.forEach((timer, key) => {
      clearInterval(timer)
      log('CacheService', 'stopAutoRefreshTimers', `Timer stopped for ${key}`)
    })
    this.refreshTimers.clear()
  }

  async checkAndRefresh(key: string, config: CacheOptions): Promise<void> {
    const entry = memoryCache.get(key) || await indexedDBAdapter.get(key)

    if (!entry) {
      log('CacheService', 'checkAndRefresh', `No entry found for ${key}`)
      return
    }

    const now = Date.now()
    const ttl = (config.ttl as number) || 0
    const refreshThreshold = entry.expiry - (ttl * 0.2)

    if (now >= refreshThreshold) {
      log('CacheService', 'checkAndRefresh', `Refreshing ${key}`)
      await this._invalidateLocal(key)
    }
  }

  async getStats(): Promise<CacheStats> {
    const dbStats = await indexedDBAdapter.getStats()

    return {
      ...cacheState.stats,
      hitRate: cacheState.stats.hits / (cacheState.stats.hits + cacheState.stats.misses) || 0,
      memoryEntries: memoryCache.size,
      ...dbStats,
      isInitialized: this.isInitialized,
      crossTabSyncAvailable: cacheSyncManager.isSyncAvailable()
    }
  }

  async has(key: string): Promise<boolean> {
    const now = Date.now()

    if (memoryCache.has(key)) {
      const entry = memoryCache.get(key)!
      return entry.expiry > now
    }

    const stored = await indexedDBAdapter.get(key)
    return !!stored && stored.expiry > now
  }

  destroy(): void {
    this.stopAutoRefreshTimers()
    cacheSyncManager.close()
    memoryCache.clear()
    indexedDBAdapter.close()

    this.isInitialized = false
    cacheState.isInitialized = false

    log('CacheService', 'destroy', 'Cache service destroyed')
  }
}

export const cacheService = new CacheService()
export { CacheService }

if (typeof window !== 'undefined') {
  ;(window as unknown as { __cacheService: CacheService }).__cacheService = cacheService
  ;(window as unknown as { __cacheStats: () => Promise<CacheStats> }).__cacheStats = () => cacheService.getStats()
}
